'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import CloseIcon from '@/fo/icons/base/icons/cross32.svg';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitDrawerBase } from '@/fo/ui/kit/drawer/base';
import { Option } from '@/fo/ui/kit/listbox/base';
import { Product } from '@/fo/ui/review/catalog/base';
import { UiReviewInformerPoints } from '@/fo/ui/review/informer/points';
import { UiReviewTileProduct } from '@/fo/ui/review/tile/product';

import { QuestionForm, RatingValue, ReviewForm } from '../components';
import { UserInfo } from '../types';

import { styles } from './FeatureReviewFormBase.styles';

export interface FeatureReviewFormBaseProps {
  isLoading: boolean;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  product: Product;
  productMindboxId: string;
  type: 'review' | 'question';
  skinTypes: Option[];
  skinProblems: Option[];
  ratingId: string;
  ratings: RatingValue[];
  userInfo?: UserInfo;
  isAuthorized?: boolean;
}

export function FeatureReviewFormBase({
  isLoading,
  onOpenChange,
  open,
  product,
  productMindboxId,
  type,
  skinTypes,
  skinProblems,
  ratingId,
  ratings,
  userInfo,
  isAuthorized
}: FeatureReviewFormBaseProps) {
  const t = useTranslations();

  const slots = styles();

  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (!open) {
      setIsSuccess(false);
    }
  }, [open]);

  const title =
    type === 'review' ? t('review.form.title') : t('review.form.questionTitle');

  const children = (
    <>
      <div className={slots.header()}>
        {!isSuccess ? <h2 className={slots.title()}>{title}</h2> : <div />}
        <UiKitButtonBase
          color="ghost"
          rounded="full"
          className={slots.mobileCloseButton()}
          onClick={() => onOpenChange(false)}
        >
          <CloseIcon className={slots.closeIcon()} />
        </UiKitButtonBase>
      </div>

      {!isSuccess && type === 'review' && (
        <UiReviewInformerPoints isNewProduct={product.isNewProduct} />
      )}

      {!isSuccess && (
        <UiReviewTileProduct
          title={product.title}
          imageUrl={product.imageUrl}
          tags={
            product.volume
              ? Array.isArray(product.volume)
                ? product.volume
                : [product.volume]
              : []
          }
        />
      )}

      {type === 'review' && (
        <ReviewForm
          sku={product.id}
          mindboxProductId={productMindboxId}
          ratingId={ratingId}
          ratings={ratings}
          skinTypes={skinTypes}
          skinProblems={skinProblems}
          onSuccess={() => setIsSuccess(true)}
          userInfo={userInfo}
          onClose={() => onOpenChange(false)}
          isAuthorized={isAuthorized}
        />
      )}
      {type === 'question' && (
        <QuestionForm
          skinTypes={skinTypes}
          skinProblems={skinProblems}
          sku={product.id}
          onSuccess={() => setIsSuccess(true)}
          userInfo={userInfo}
          onClose={() => onOpenChange(false)}
        />
      )}
    </>
  );

  return (
    <UiKitDrawerBase
      title={title}
      open={open && !isLoading}
      direction="right"
      withoutMobile
      onOpenChange={onOpenChange}
      handleOnly
      contentClassName={slots.content()}
      closeButtonClassName={slots.closeButton()}
    >
      {!isLoading && children}
    </UiKitDrawerBase>
  );
}
